import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../../lib/api";
import { session } from "../../lib/session";
import { st } from "../../styles.js";
import { Icon } from "../../icons";
import { BuzerIcon } from "../../BuzerIcons";
import { Avatar, FriendChip, FriendPickerHint, Glyph, Modal, FormShell, FormFooter, ConfirmDelete, Chk, Timer, Stopwatch, DateField, useProfileModal } from "../../components/ui";
import { Button, Field, Input, Select, IconButton, SectionHeader, Caption, Tag, StateDot, DescrBox } from "../../components/ui/ds";
import { IconColorModal } from "../../components/forms/IconColorModal";
import { ReminderPrompt } from "../../components/forms/ReminderPrompt";
import { iconChipStyle } from "../../lib/icons-resolve";
import { Stat, FirstWeekNote } from "../buzer/TaskDetail";
import { LIFE_CATS, LIFE_CAT } from "../../lib/constants";
import { Pill, Segmented } from "../../components/ui/ds";
import { todayKey, WEEKDAYS_SK, fmtDur, parseDur } from "../../lib/format";
import { DUR_PRESETS, DUR_OPTIONS, SUGGEST_ICONS } from "../../lib/constants";

// Challenge/competition sub-system: leaderboard board, create/edit form, competition detail.
// Used by BuzerTab (features/buzer) and FriendsTab (features/social).

// v26 §3.12 — popis výzvy/súťaže v zvýraznenom rámiku; DescrBox je teraz zdieľaný primitív (ds/DescrBox),
// aby detail návyku aj výzvy/súťaže používali rovnaký vzhľad.

// v26 §3.9 — vlastné pripomenutie výzvy per člen: číta/zapisuje remind_at MOJHO backing tasku
function ChallengeRemind({ taskId, value }) {
  const [ra, setRa] = useState(value || "");
  const save = async (next) => {
    setRa(next);
    try { await api("/api/tasks/" + taskId, { method: "PATCH", body: { remind_at: next || null } }); } catch (e) { /* */ }
  };
  const [hh, mm] = (ra || "08:00").split(":");
  const mins = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0"));
  if (mm && !mins.includes(mm)) mins.push(mm);
  return (
    <div className="bz-remind">
      {/* v-tile — hlavička ako pilulka (.bz-tile): label vľavo, prepínač vpravo VNÚTRI pilulky */}
      <div className="bz-remind__pill">
        <span className="bz-tile__icon"><BuzerIcon name="bell" size={16} color="currentColor" /></span>
        <span className="bz-remind__label">Upozorniť ma v čase</span>
        <button type="button" role="switch" aria-checked={!!ra} aria-label="Zapnúť pripomenutie"
          onClick={() => save(ra ? "" : "08:00")}
          style={{ ...st.switch, ...(ra ? st.switchOn : {}), flex: "none" }}>
          <span style={{ ...st.switchKnob, ...(ra ? st.switchKnobOn : {}) }} />
        </button>
      </div>
      {!!ra && (
        <div className="bz-remind__times">
          <Select value={hh} style={{ width: 96, fontSize: 16 }} aria-label="Hodina" onChange={(e) => save(e.target.value + ":" + (mm || "00"))}>
            {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0")).map((h) => <option key={h} value={h}>{h}</option>)}
          </Select>
          <span className="bz-remind__sep">:</span>
          <Select value={mm} style={{ width: 96, fontSize: 16 }} aria-label="Minúta" onChange={(e) => save((hh || "08") + ":" + e.target.value)}>
            {mins.sort().map((m) => <option key={m} value={m}>{m}</option>)}
          </Select>
        </div>
      )}
      {!!ra && <div style={{ ...st.fieldHint, marginTop: 6 }}>Príde len tebe — každý člen si nastavuje vlastný čas.</div>}
    </div>
  );
}

export function ChallengeBoard({ cid, onChanged, reload, inModal, onRequestUncheck, lite = false, refreshKey = 0 }) {
  const { open: openPublicProfile } = useProfileModal();  // globálny modal verejného profilu
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [delAsk, setDelAsk] = useState(false);  // v31 — „Zmazať výzvu?" potvrdenie s „Radšej archivovať"
  // §7.4 — in the habit modal show a compact mini board (top 3 + me); elsewhere the full list
  const load = useCallback(async () => { setData(await api("/api/challenges/" + cid + (inModal ? "?board=mini" : ""))); }, [cid, inModal]);
  useEffect(() => { load(); }, [load]);
  // ITEM 3 — rodič bumpne refreshKey po zrušení splnenia (uncheck cez toggle), aby sa doneToday
  // v tomto boarde znovu načítalo a tlačidlo sa vrátilo na „Splniť dnes"
  useEffect(() => { if (refreshKey) load(); }, [refreshKey]);  // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return <div style={{ display: "flex", justifyContent: "center", padding: 16 }}><div className="spin" /></div>;
  if (editing) return (
    <div style={inModal ? {} : st.boardCard}>
      <ChallengeCreate
        challenge={{ id: cid, name: data.name, icon: data.icon, icon_color: data.icon_color, desc: data.descr, points: data.points,
          timer_sec: data.timer_sec, timer_min: data.timer_min,  // fix — inak sa uložený časovač v Upraviť nepredvyplní
          freq_per_week: data.freq_per_week || 0, freq_days: data.freq_days, duration_days: data.duration_days, category: data.category }}
        memberIds={data.members.map((m) => m.user_id)}
        onAdded={() => { load(); reload && reload(); }}
        onDone={() => setEditing(false)} />
    </div>
  );

  async function leave() { if (!confirm("Opustiť výzvu? Tvoj návyk sa skryje a v archíve sa neuloží.")) return; await api("/api/challenges/" + cid + "/leave", { method: "DELETE" }); onChanged(); }
  async function del() { if (!confirm("Zmazať výzvu pre všetkých účastníkov?")) return; await api("/api/challenges/" + cid, { method: "DELETE" }); onChanged(); }
  // v26 — archivácia: tvorca ukončí k dnešku pre všetkých; člen odíde, ale ostane mu v archíve
  async function archiveAll() { if (!confirm("Ukončiť výzvu k dnešku pre všetkých členov? Presunie sa do archívu s výsledkami.")) return; await api("/api/challenges/" + cid + "/archive", { method: "POST", body: { scope: "all" } }); onChanged(); }
  async function archiveMe() { if (!confirm("Archivovať výzvu u teba? Odídeš z nej, ale výsledky ti ostanú v archíve.")) return; await api("/api/challenges/" + cid + "/archive", { method: "POST", body: { scope: "me" } }); onChanged(); }
  // v17 — complete (toggle) today's challenge habit straight from the výzva modal
  async function completeToday() {
    const meRow = data && (data.members || []).find((m) => m.is_me);
    const tid = (meRow && meRow.task_id) || (data && data.me && data.me.task_id);
    if (!tid) return;
    try { await api("/api/toggle", { method: "POST", body: { task_id: tid, date: todayKey() } }); load(); reload && reload(); } catch (e) { /* */ }
  }
  const ctrlRow = (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
      {data.is_creator
        ? <button onClick={() => setEditing(true)} style={{ ...st.linkBtn, display: "inline-flex", alignItems: "center", gap: 4 }}><Icon name="edit" size={14} color="currentColor" /> Upraviť výzvu</button>
        : <span />}
      {data.is_creator
        ? <span style={{ display: "inline-flex", gap: 14 }}>
            <button onClick={archiveAll} style={st.linkBtn}>Archivovať</button>
            <button onClick={del} style={st.linkDanger}>Zmazať výzvu</button>
          </span>
        : <span style={{ display: "inline-flex", gap: 14 }}>
            <button onClick={archiveMe} style={st.linkBtn}>Archivovať u mňa</button>
            <button onClick={leave} style={st.linkDanger}>Opustiť výzvu</button>
          </span>}
    </div>
  );

  // §7.4 / v17 — výzva mini board (mock #5): desc+points, rebríček top-3 + me, Tvoje miesto, Splniť dnes / Opustiť
  if (data.mini) {
    const leader = data.leader_points || 1;
    const meRow = (data.members || []).find((m) => m.is_me) || data.me;
    const meShown = (data.members || []).some((m) => m.is_me);
    // v22 — 7-dňový prúžok pre viditeľných účastníkov (ktoré dni splnili výzvu)
    const last7v = [];
    for (let i = 6; i >= 0; i--) { const d = new Date(); d.setDate(d.getDate() - i); last7v.push(d.toISOString().slice(0, 10)); }
    const dayStrip = (hist) => (
      <div style={{ display: "flex", gap: 3, marginTop: 7 }}>
        {last7v.map((k) => <span key={k} title={k} style={{ flex: 1, height: 6, borderRadius: 3, background: (hist && hist[k] > 0) ? "var(--accent)" : "var(--accent-tint)" }} />)}
      </div>
    );
    const rankBadge = (rank) => (
      <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: 8, flexShrink: 0, fontFamily: "var(--font-mono)", fontWeight: 800, fontSize: 12.5,
        ...(rank === 1 ? { background: "var(--accent)", color: "var(--on-ember)" } : { background: "var(--surface)", border: "1px solid var(--border)", color: "var(--text2)" }) }}>{rank}</span>
    );
    // v19 — each row is a padded, bordered card (mock #17): side padding, smaller rank/value
    const bar = (m) => (
      <div key={m.user_id} style={{ display: "flex", alignItems: "center", gap: 11, padding: "14px 14px", borderRadius: 16, marginBottom: 10, border: "1px solid " + (m.is_me ? "var(--accent-tint)" : "var(--border)"), background: m.is_me ? "var(--ember-soft)" : "var(--surface-solid)" }}>
        {rankBadge(m.rank)}
        <Avatar user={m} size={30} onClick={() => openPublicProfile(m.user_id)} />
        <div style={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={() => openPublicProfile(m.user_id)}>
          <div style={{ ...st.boardName, display: "inline-flex", alignItems: "center", gap: 6 }}>{m.name}{m.is_me ? <Tag tone="neutral" size="xs">TY</Tag> : null}{m.doneToday ? <StateDot state="done" size={13} /> : null}</div>
          <div style={{ height: 6, borderRadius: 99, background: "var(--accent-tint)", marginTop: 5, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${Math.max(4, (m.totalPoints / leader) * 100)}%`, background: "var(--accent-grad)" }} />
          </div>
          {m.history ? dayStrip(m.history) : null}
        </div>
        <span style={{ ...st.taskDesc, flexShrink: 0, fontFamily: "var(--font-mono)", fontSize: 12.5, alignSelf: "flex-start" }}>{m.totalPoints}×</span>
      </div>
    );
    return (
      <div style={inModal ? {} : st.boardCard}>
        {/* v26 §3.12 — popis v rámiku NAD dlaždicami */}
        <DescrBox text={data.descr} style={{ marginBottom: 16 }} />
        {/* v2 — same Stat tiles as the návyk detail (body · frekv. · séria · účastník) */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
          {data.points != null && <Stat icon={<span style={{ fontFamily: "var(--ff-mono)", fontWeight: 700, fontSize: 17, lineHeight: 1, color: "var(--brand-primary)" }}>+</span>} value={data.points} valueColor="var(--brand-primary)" label="BODY" />}
          <Stat icon={<BuzerIcon name="calendar" size={17} color="currentColor" />} value={data.freq_per_week ? `${data.freq_per_week}× týž.` : (data.freq_days ? String(data.freq_days).split(",").map((n) => WEEKDAYS_SK[Number(n) - 1]).join("·") : "Denne")} label="FREKV." />
          {/* v33h — Lite (bez bodov) má sériu vo farbe témy, nie streak-oranžovú */}
          <Stat icon={<BuzerIcon name="flame" size={17} color={lite ? "var(--brand-primary)" : "var(--streak)"} />} value={(() => {
            const sr = (meRow && meRow.streak) || 0;
            // v26.3 — týždenná výzva ukazuje sériu v týždňoch (rovnako ako návyk)
            return (meRow && meRow.streak_unit) === "week"
              ? `${sr} ${sr === 1 ? "týždeň" : (sr >= 2 && sr <= 4 ? "týždne" : "týždňov")}`
              : `${sr} ${sr === 1 ? "deň" : (sr >= 2 && sr <= 4 ? "dni" : "dní")}`;
          })()} valueColor={lite ? "var(--brand-primary)" : "var(--streak)"} label="SÉRIA" />
          <Stat icon={<BuzerIcon name="people" size={17} color="currentColor" />} value={data.total} label="ÚČASTNÍCI" />
          {/* v32/v33 — životná kategória + koľko zostáva do konca výzvy (kategória z jednotného
              `life_cat`, fallback na `category`) — v jednom balenom riadku spolu s ostatnými pilulkami */}
          {LIFE_CAT[data.life_cat || data.category] && (
            <Stat icon={<BuzerIcon name={LIFE_CAT[data.life_cat || data.category].icon} size={17} color="currentColor" />} value={LIFE_CAT[data.life_cat || data.category].label} label="KATEGÓRIA" />
          )}
          {data.days_left != null && (
            <Stat icon={<BuzerIcon name="hourglass" size={17} color="currentColor" />} value={`${data.days_left} ${data.days_left === 1 ? "deň" : (data.days_left >= 2 && data.days_left <= 4 ? "dni" : "dní")}`} label="DO KONCA" />
          )}
          {/* v34 — pripomienková pilulka je súčasťou skupiny piluliek (neodskakuje na nový riadok) */}
          {data.my_task_id ? <ChallengeRemind key={"rem" + data.my_task_id} taskId={data.my_task_id} value={data.my_remind_at} /> : null}
          {/* dosiahnutá frekvencia = ROVNAKÁ pilulka (bz-tile) ako v detaile návyku: bodky sú „ikona",
              N/N je hodnota, „splnené" label. Cieľ (week_target) je pomerový v prvom neúplnom týždni
              (Plán A) → zhoduje sa s miniatúrou na Dnes; plný cieľ vysvetľuje bublina pod dlaždicami. */}
          {data.freq_per_week > 0 && (
            <Stat
              icon={<span style={{ display: "inline-flex", gap: 4 }}>
                {Array.from({ length: data.week_target || 0 }).map((_, i) => (
                  <span key={i} style={{ width: 9, height: 9, borderRadius: "50%", background: i < (data.week_done || 0) ? "var(--brand-primary)" : "transparent", border: i < (data.week_done || 0) ? "none" : "1.5px solid var(--neutral-border-strong)" }} />
                ))}
              </span>}
              value={`${data.week_done || 0}/${data.week_target}`}
              label="splnené"
            />
          )}
        </div>
        {/* v35 — zdieľaná „prvý týždeň" bublina (rovnaká ako v detaile návyku): vysvetlí znížený
            cieľ prvého neúplného týždňa. Bez nej pôsobí „2×" pri nastavenej 4×/týždeň ako chyba. */}
        {data.freq_per_week > 0 && <FirstWeekNote target={data.week_target || 0} full={data.week_full || 0} style={{ marginTop: 0, marginBottom: 16 }} />}
        <SectionHeader label="Rebríček" style={{ margin: "2px 2px 12px" }}
          count={`${data.total} ${data.total === 1 ? "účastník" : (data.total >= 2 && data.total <= 4 ? "účastníci" : "účastníkov")}`} />
        {data.members.map(bar)}
        {!meShown && data.me && (<>
          <div style={{ textAlign: "center", color: "var(--muted)", letterSpacing: 2, margin: "-2px 0 2px" }}>···</div>
          {bar(data.me)}
        </>)}
        {data.my_rank ? <div style={{ ...st.taskDesc, textAlign: "center", marginTop: 14 }}>Tvoje miesto: <strong style={{ color: "var(--text)" }}>{data.my_rank}.</strong> z {data.total}</div> : null}
        {/* v2 — footer like the návyk detail: Splniť dnes full-width, Upraviť (or Opustiť) below.
            v33c — v modáli = sticky pätička (bz-sheet-stickyfoot), nech tlačidlá neodídu pod rebríčkom. */}
        <div className={inModal ? "bz-sheet-stickyfoot" : undefined}
          style={inModal ? undefined : { display: "flex", flexDirection: "column", gap: 9, marginTop: 18 }}>
          <Button variant="primary" block onClick={() => ((meRow && meRow.doneToday) ? (onRequestUncheck ? onRequestUncheck() : completeToday()) : completeToday())}>
            {meRow && meRow.doneToday ? <><Icon name="check" size={19} color="currentColor" stroke={2.6} />Splnené dnes</> : <><Icon name="check" size={19} color="currentColor" stroke={2.6} />Splniť dnes</>}
          </Button>
          {/* v2 — časované výzvy (napr. meditácia/čítanie): odpočet, po doznení sa dnešok sám splní.
              variant="button" — primárne tlačidlo „Spustiť časovač" ROVNAKO ako v detaile návyku
              (TaskDetail); po spustení sa prepne na sekundárne. onComplete ostáva (auto-splnenie). */}
          {data.timer_sec > 0 && !(meRow && meRow.doneToday) && (
            <Timer variant="button" id={"chal" + cid} seconds={data.timer_sec} onComplete={completeToday} />
          )}
          {data.is_creator
            ? <div style={{ display: "flex", gap: 8 }}>
                <Button variant="ghost" block onClick={() => setEditing(true)}><Icon name="edit" size={16} color="currentColor" />Upraviť</Button>
                <IconButton variant="ghost" aria-label="Zmazať výzvu" title="Zmazať výzvu" onClick={() => setDelAsk(true)} style={{ color: "var(--danger)" }}><Icon name="delete" size={16} color="currentColor" /></IconButton>
              </div>
            : <div style={{ display: "flex", gap: 8 }}>
                <Button variant="ghost" block onClick={archiveMe} title="Odísť, ale nechať si výsledky v archíve"><BuzerIcon name="folder" size={16} color="currentColor" />Archivovať u mňa</Button>
                <Button variant="ghost" block onClick={leave} style={{ color: "var(--danger)" }}><Icon name="logout" size={16} color="currentColor" />Opustiť</Button>
              </div>}
        </div>
        {/* v31 — „Zmazať výzvu?" cez zdieľané ConfirmDelete (archív = primary, jedno miesto v kóde) */}
        {delAsk && (
          <ConfirmDelete title="Zmazať výzvu?" icon="trash"
            message={<>Archivovaním sa výzva ukončí k dnešku a <strong>všetkým ostane v archíve</strong>. Zmazaním zmizne u teba — ostatným účastníkom sa <strong>automaticky archivuje</strong> s výsledkami.</>}
            archiveLabel="Archivovať pre všetkých" deleteLabel="Zmazať u mňa"
            onArchive={async () => { setDelAsk(false); await api("/api/challenges/" + cid + "/archive", { method: "POST", body: { scope: "all" } }); onChanged(); }}
            onDelete={async () => { setDelAsk(false); await api("/api/challenges/" + cid, { method: "DELETE" }); onChanged(); }}
            onClose={() => setDelAsk(false)} />
        )}
      </div>
    );
  }

  const last7 = [];
  for (let i = 6; i >= 0; i--) { const d = new Date(); d.setDate(d.getDate() - i); last7.push(d.toISOString().slice(0, 10)); }
  let maxPts = 1;
  for (const m of data.members) for (const k of last7) maxPts = Math.max(maxPts, m.history[k] || 0);

  return (
    <div style={inModal ? {} : st.boardCard}>
      <div style={st.boardHead}>
        {inModal ? <span /> : <span style={st.boardTitle}>{data.icon} {data.name} · rebríček</span>}
        {data.invited_count > 0 && <span style={{ ...st.taskDesc, display: "inline-flex", alignItems: "center", gap: 5 }}><BuzerIcon name="hourglass" size={13} color="currentColor" />{data.invited_count} pozvaných</span>}
      </div>
      <DescrBox text={data.descr} style={{ marginBottom: 10 }} />
      {data.members.map((m, idx) => (
        <div key={m.user_id} style={{ ...st.boardRow, ...(m.is_me ? { background: "var(--ember-soft)", borderRadius: 12 } : {}) }}>
          {/* v18 §9 — unified rank badge */}
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: 9, flexShrink: 0, fontFamily: "var(--font-mono)", fontWeight: 800, fontSize: 13,
            ...(idx === 0 ? { background: "var(--accent)", color: "var(--on-ember)" } : { background: "var(--surface)", border: "1px solid var(--border)", color: "var(--text2)" }) }}>{idx + 1}</span>
          <Avatar user={m} size={28} onClick={() => openPublicProfile(m.user_id)} />
          <div style={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={() => openPublicProfile(m.user_id)}>
            <div style={{ ...st.boardName, display: "inline-flex", alignItems: "center", gap: 6 }}>{m.name}{m.is_me ? <Tag tone="neutral" size="xs">TY</Tag> : null}{m.doneToday ? <StateDot state="done" size={13} /> : null}</div>
            <div style={st.taskDesc}><BuzerIcon name="flame" size={12} color="var(--muted)" style={{ verticalAlign: "-2px", marginRight: 3 }} />{m.streak} · {m.totalPoints}×</div>
          </div>
          <div style={st.miniChart}>
            {last7.map((k) => {
              const p = m.history[k] || 0;
              return (
                <div key={k} style={st.miniTrack}>
                  <div style={{ ...st.miniBar, height: `${Math.max(8, (p / maxPts) * 100)}%`,
                    background: p > 0 ? "var(--accent-grad)" : "var(--accent-tint)" }} />
                </div>
              );
            })}
          </div>
        </div>
      ))}
      {ctrlRow}
    </div>
  );
}

export function ChallengeCreate({ onAdded, challenge, memberIds, onDone, mode, open: openProp, onOpenChange, initialInvite }) {
  const editing = !!challenge;
  // v26.2 §6 — jeden typ Výzva; „Zapisovanie" volí spôsob (Splnenie=výzva / Vlastný výsledok=súťaž).
  const [isComp, setIsComp] = useState(editing ? !!challenge.is_competition : (mode === "competition"));
  // controlled open (single-open coordination); drafts kept in local state when collapsed.
  const controlled = openProp !== undefined;
  const [openState, setOpenState] = useState(editing);
  const open = controlled ? openProp : openState;
  const setOpen = (v) => { if (controlled) { onOpenChange && onOpenChange(v); } else setOpenState(v); };
  const [icon, setIcon] = useState(editing ? (challenge.icon || (isComp ? "trophy" : "flag")) : (isComp ? "trophy" : "flag"));
  const [iconColor, setIconColor] = useState(editing ? (challenge.icon_color || "") : "");  // v26 §3.14
  const [iconPick, setIconPick] = useState(false);  // v8 BuzerIcon picker
  const [name, setName] = useState(editing ? challenge.name : "");
  const [desc, setDesc] = useState(editing ? (challenge.desc || "") : "");
  const [pts, setPts] = useState(editing ? challenge.points : 15);
  const [freq, setFreq] = useState(editing ? (challenge.freq_per_week || 0) : 0);  // v2 — 0=denne, else N×/týž.
  const [freqDays, setFreqDays] = useState(editing && challenge.freq_days ? String(challenge.freq_days).split(",").map(Number) : []);  // v26 §3.7 — Vlastné dni
  const [duration, setDuration] = useState(editing ? (challenge.duration_days || 0) : 7);  // v14 default Týždeň; v26 §3.16 — NULL/0 pri editácii = Bez limitu
  const [durCustom, setDurCustom] = useState(!!(editing && challenge.duration_days && !DUR_PRESETS.includes(challenge.duration_days)));
  const [metricLabel, setMetricLabel] = useState(editing ? (challenge.metric_label || "") : "");
  const [metricDir, setMetricDir] = useState(editing ? (challenge.metric_dir || "high") : "high");
  const [resultTimer, setResultTimer] = useState(editing ? !!challenge.result_timer : false);  // v2 — stopky merajú výsledok
  const [multiDay, setMultiDay] = useState(editing ? !!challenge.multi_day : false);  // v2 — viac zápisov za deň (sčítavajú sa)
  const [evalDate, setEvalDate] = useState(editing ? (challenge.eval_date || "") : "");
  const _initT = editing ? (challenge.timer_sec || (challenge.timer_min || 0) * 60) : 0;
  const [timerMin, setTimerMin] = useState(Math.floor(_initT / 60));
  const [timerSec, setTimerSec] = useState(_initT % 60);
  const [friends, setFriends] = useState([]);
  const [invite, setInvite] = useState(initialInvite || []);  // v23 — preselect a friend (from their profile)
  const [friendQuery, setFriendQuery] = useState("");  // G5: search within the friend picker
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [category, setCategory] = useState(editing ? (challenge.category || "") : "");  // v32 — životná kategória
  const [catTried, setCatTried] = useState(false);  // G2a — pokus o odoslanie bez kategórie → zvýrazni výber piluliek
  const [lifeCatDialog, setLifeCatDialog] = useState(null);  // v32 — AI návrh kategórie { busy } | { category }
  const [inventOpen, setInventOpen] = useState(false);  // v2 — AI invent-whole dialog
  const [remindPrompt, setRemindPrompt] = useState(null);  // v2 — po vytvorení výzvy ponúkni pripomienku
  const [err, setErr] = useState("");
  useEffect(() => { if (open) api("/api/friends").then((d) => setFriends(d.friends || [])).catch(() => {}); }, [open]);
  const invitable = friends.filter((f) => !(memberIds || []).includes(f.friend_id));
  function toggleInv(id) { setInvite((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]); }
  const [descBusy, setDescBusy] = useState(false);   // v14 — field AI buttons
  const [ptsBusy, setPtsBusy] = useState(false);
  const [ptsDialog, setPtsDialog] = useState(null);  // v2 — "AI navrhuje X bodov" confirm modal
  async function aiFill(forceInvent) {
    setErr(""); setAiBusy(true);
    try {
      if (forceInvent || !name.trim()) {
        // v14 — no title: invent the WHOLE challenge/competition from the user's memory + data
        const r = await api("/api/ai/propose-challenge", { method: "POST", body: { is_competition: isComp, icons: SUGGEST_ICONS } });
        const ch = r.challenge || {};
        if (ch.name) setName(ch.name);
        if (ch.icon) setIcon(ch.icon);
        if (ch.desc) setDesc(ch.desc);
        if (isComp) {
          if (ch.metric_label) setMetricLabel(ch.metric_label);
          if (ch.metric_dir) setMetricDir(ch.metric_dir);
        } else if (ch.points) { setPts(ch.points); }
      } else {
        // draft the whole challenge/competition from the title: icon (from the set), description, points (výzva)
        const r = await api("/api/ai/estimate-habit", { method: "POST", body: { label: name, icons: SUGGEST_ICONS } });
        if (r.icon) setIcon(r.icon);
        if (r.desc !== undefined) setDesc(r.desc);
        if (!isComp && r.points) setPts(r.points);
      }
    } catch (e) { setErr(e.message); }
    setAiBusy(false);
  }
  async function aiChDesc() {  // v14 — popis-only AI
    if (!name.trim()) { setErr("Najprv napíš názov."); return; }
    setErr(""); setDescBusy(true);
    try { const r = await api("/api/ai/estimate-habit", { method: "POST", body: { label: name, icons: SUGGEST_ICONS } }); if (r.desc) setDesc(r.desc); } catch (e) { setErr(e.message); }
    setDescBusy(false);
  }
  async function aiChPoints() {  // v14 — body-only AI (rubric); v2 — show the "AI navrhuje" modal (ako pri Pridať návyk)
    if (!name.trim()) { setErr("Najprv napíš názov."); return; }
    setErr(""); setPtsBusy(true); setPtsDialog({ busy: true, points: null });
    try { const r = await api("/api/ai/estimate-habit", { method: "POST", body: { label: name, icons: SUGGEST_ICONS } }); setPtsDialog({ busy: false, points: r.points || Number(pts) || 15 }); }
    catch (e) { setErr(e.message); setPtsDialog(null); }
    setPtsBusy(false);
  }
  // v32 — AI návrh životnej kategórie (potvrdenie rovnaké ako pri bodoch)
  async function openLifeCatDialog() {
    if (!name.trim()) { setErr("Najprv napíš názov."); return; }
    setErr(""); setLifeCatDialog({ busy: true });
    try {
      const r = await api("/api/ai/suggest-life-category", { method: "POST", body: { label: name, desc } });
      if (!r.category) { setErr("AI nevedela kategóriu určiť — vyber ju ručne."); setLifeCatDialog(null); return; }
      setLifeCatDialog({ category: r.category });
    } catch (e) { setErr(e.message); setLifeCatDialog(null); }
  }
  const chHeaderAi = session.ai && !editing ? (
    <button type="button" onClick={() => setInventOpen(true)} title="Vymyslieť výzvu (AI)" aria-label="Vymyslieť"
      style={{ border: "none", background: "transparent", color: "var(--accent)", display: "inline-flex", alignItems: "center", cursor: "pointer", padding: 4 }}>
      <BuzerIcon name="sparkles" size={18} color="currentColor" /></button>
  ) : null;
  function close() { if (editing) { onDone && onDone(); } else { setOpen(false); } }
  // v2 — delete lives in the edit form (like the návyk edit modal), not in the detail
  async function del() {
    if (!confirm("Zmazať výzvu u teba? Ostatným účastníkom sa automaticky archivuje s výsledkami.")) return;
    try { await api("/api/challenges/" + challenge.id, { method: "DELETE" }); onAdded(); onDone && onDone(); } catch (e) { /* */ }
  }
  async function submit(e) {
    e.preventDefault(); setErr("");
    // G2a — životná kategória je POVINNÁ pre výzvu aj súťaž; bez nej neposielaj na API
    if (!category) { setCatTried(true); setErr("Vyber kategóriu — je povinná."); return; }
    setBusy(true);
    try {
      const body = { name, icon, icon_color: iconColor || null, desc, points: pts, invite, is_competition: isComp,
        category: category || null,  // v32 — životná kategória
        timer_sec: (Number(timerMin) || 0) * 60 + (Number(timerSec) || 0),  // pri stopkách sa countdown nepoužije, ale nenuluj ho
        result_timer: isComp && resultTimer,
        multi_day: isComp && multiDay,
        metric_label: (isComp && resultTimer) ? "čas" : metricLabel, metric_dir: metricDir, eval_date: isComp ? evalDate : "", duration_days: isComp ? 0 : (Number(duration) || 0),
        freq_per_week: isComp ? 0 : Number(freq) || 0,
        freq_days: isComp || freq > 0 ? [] : freqDays };  // v26 §3.7
      let offer = null;
      if (editing) await api("/api/challenges/" + challenge.id, { method: "PATCH", body });
      else {
        const r = await api("/api/challenges", { method: "POST", body });
        // v2 — nová výzva (nie súťaž) → ponúkni pripomienku na jej backing tasku
        if (!isComp && r && r.task_id) offer = { taskId: r.task_id, label: name };
      }
      if (!editing) { setName(""); setDesc(""); setPts(15); setIcon(isComp ? "🥇" : "🤝"); setInvite([]); setMetricLabel(""); setEvalDate(""); setTimerMin(0); setTimerSec(0); setResultTimer(false); setMultiDay(false); if (!offer) setOpen(false); }
      // zavretie + onAdded/onDone sa pri ponuke pripomienky ODKLADAJÚ až po popupe —
      // rodičia na ne unmountujú (napr. výzva z profilu priateľa) a popup by zmizol
      if (offer) { setRemindPrompt(offer); setBusy(false); return; }
      onAdded(); onDone && onDone();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }
  // v2 — pripomienkový popup: kým je otvorený, formulár sa nezatvára (odložené callbacky vyššie)
  const remindPromptEl = remindPrompt ? (
    <ReminderPrompt taskId={remindPrompt.taskId} label={remindPrompt.label}
      onClose={() => { setRemindPrompt(null); setOpen(false); onAdded(); onDone && onDone(); }} />
  ) : null;
  if (!open) return <>{remindPromptEl}{controlled ? null : <button onClick={() => setOpen(true)} style={st.addToggle}><BuzerIcon name={isComp ? "trophy" : "flag"} size={16} /> Vytvoriť výzvu</button>}</>;
  const inModal = open;  // v2 — editing also renders as the same modal shell as create (just prefilled)
  return (
    <>
    {remindPromptEl}
    <FormShell modal={inModal} title={editing ? "Upraviť výzvu" : "Vytvoriť výzvu"} onClose={close} headerAction={inModal && !editing ? chHeaderAi : null}
      iconNode={<Glyph icon={icon || (isComp ? "trophy" : "flag")} size={20} />} iconStyle={iconChipStyle(iconColor)} onIconClick={() => setIconPick(true)}>
    <form onSubmit={submit} className={inModal ? "bz-modal-form" : undefined} style={inModal ? undefined : { ...st.card, marginTop: 12, ...st.formCol }}>
      {/* v26.2 §6 — Zapisovanie: Splnenie (odškrtnutie) / Vlastný výsledok (číslo). Po vytvorení nemenné.
          v34 — rovnaký Segmented ako Pripomienka/Udalosť vo formulári pripomienky (zdieľaný primitív). */}
      <Field label="Zapisovanie">
        <Segmented className="bz-seg--block" disabled={editing}
          options={[{ value: "done", label: "Splnenie" }, { value: "result", label: "Vlastný výsledok" }]}
          value={isComp ? "result" : "done"}
          onChange={(v) => { if (!editing) setIsComp(v === "result"); }} />
      </Field>
      <Field label={isComp ? "Názov (čo sa meria) *" : "Názov výzvy *"}>
        <Input placeholder={isComp ? "napr. Kto nabehá viac km" : "napr. 30 dní bez cukru"}
          value={name} onChange={(e) => setName(e.target.value)} required />
        {iconPick && (
          <IconColorModal icon={icon} iconColor={iconColor} fallback={isComp ? "trophy" : "flag"}
            onIcon={setIcon} onColor={setIconColor} onClose={() => setIconPick(false)} />
        )}
      </Field>
      <Field label="Špecifikácia">
        <Input placeholder="napr. kto vydrží dlhšie" value={desc} onChange={(e) => setDesc(e.target.value)} />
      </Field>
      {/* v32 — ŽIVOTNÁ KATEGÓRIA (rovnaká sada + AI návrh ako pri návyku) */}
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 7 }}>
          <span className="bz-field__label" style={{ marginBottom: 0 }}>Kategória *</span>
          {session.ai && <button type="button" onClick={openLifeCatDialog} title="Navrhnúť kategóriu (AI)" aria-label="Navrhnúť kategóriu"
            style={{ border: "none", background: "transparent", color: "var(--accent)", display: "inline-flex", cursor: "pointer", padding: 0 }}><BuzerIcon name="sparkles" size={15} color="currentColor" /></button>}
        </div>
        <div className={`bz-cat-pills${catTried && !category ? " bz-cat-pills--invalid" : ""}`}>
          {LIFE_CATS.map((c) => (
            <Pill key={c.key} on={category === c.key} onClick={() => { setCategory(category === c.key ? "" : c.key); setCatTried(false); }}>
              <BuzerIcon name={c.icon} size={15} color="currentColor" />{c.label}
            </Pill>
          ))}
        </div>
        {/* G2a — kategória je POVINNÁ pre výzvu aj súťaž; výrazné upozornenie kým nie je vybraná
            (nahradilo drobný Caption, ktorý bol prehliadnuteľný) */}
        {!category && (
          <div className="bz-cat-notice" role="alert">
            <span className="bz-cat-notice__dot" />Vyber kategóriu — je povinná.
          </div>
        )}
      </div>
      {isComp ? (
        <>
          {/* stopky/režim dňa sa dajú zmeniť aj po vytvorení — doterajšie zápisy (výsledky) sa NEmažú.
              Pri zapnutí stopiek sa existujúce čísla čítajú ako čas (sekundy). */}
          <label style={st.inlineLabel}><span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><BuzerIcon name="stopwatch" size={16} color="var(--text2)" /> Stopky merajú výsledok</span>
            <button type="button" role="switch" aria-checked={resultTimer}
              onClick={() => setResultTimer((v) => { if (!v) setMultiDay(true); return !v; })}
              style={{ ...st.switch, flex: "none", cursor: "pointer", ...(resultTimer ? st.switchOn : {}) }}><span style={{ ...st.switchKnob, ...(resultTimer ? st.switchKnobOn : {}) }} /></button>
          </label>
          {resultTimer ? (
            <div style={st.taskDesc}>Účastník spustí stopky, po zastavení sa čas uloží ako výsledok (napr. 4:35 alebo 1:23:45).</div>
          ) : (
            <Input placeholder="Čo sa meria? (napr. čas v s, počet, km)"
              value={metricLabel} onChange={(e) => setMetricLabel(e.target.value)} />
          )}
          {/* režim dňa: jeden zápis (prepíše sa) vs. viac zápisov (sčítavajú sa) — meniteľné aj po vytvorení */}
          <Field label="Zápis za deň">
            <div style={{ display: "flex", gap: 6, background: "var(--neutral-surface-3)", padding: 4, borderRadius: "var(--radius-pill)" }}>
              {[[false, "Jeden"], [true, "Viac — sčítavajú sa"]].map(([val, lbl]) => {
                const active = multiDay === val;
                return (
                  <button type="button" key={String(val)} onClick={() => setMultiDay(val)}
                    style={{ flex: 1, textAlign: "center", fontSize: 12.5, fontWeight: 600, padding: "9px 0", borderRadius: "var(--radius-pill)", cursor: "pointer", border: "none", fontFamily: "var(--ff-ui)", WebkitTextFillColor: "currentColor",
                      ...(active ? { background: "var(--neutral-surface)", color: "var(--neutral-text-main)", boxShadow: "var(--elev-1)" } : { background: "transparent", color: "var(--neutral-text-muted)" }) }}>{lbl}</button>
                );
              })}
            </div>
            <div style={{ ...st.taskDesc, marginTop: 6 }}>
              {multiDay ? "Každý zápis sa pripočíta k dnešku — napr. viac tréningov či plankov za deň."
                : "Nový zápis prepíše ten dnešný — napr. najlepší výkon v jednom pokuse."}
            </div>
            {editing && <div style={{ ...st.taskDesc, marginTop: 6, color: "var(--ember)" }}>Doterajšie zápisy zostanú zachované. Pri zapnutí stopiek sa existujúce čísla zobrazia ako čas (sekundy).</div>}
          </Field>
          <label style={st.inlineLabel}><span>Vyhráva</span>
            <Select style={{ width: 170 }}
              value={metricDir} onChange={(e) => setMetricDir(e.target.value)}>
              <option value="high">vyšší výsledok</option>
              <option value="low">nižší výsledok</option>
            </Select></label>
          <label style={st.inlineLabel}><span>Dátum vyhodnotenia</span>
            <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
              {/* v26.3 §1 — kalendárový picker (čistý dátum) */}
              <DateField value={evalDate} onChange={setEvalDate} allowClear label="Dátum vyhodnotenia" />
            </span></label>
          <label style={st.inlineLabel}><span>Body pre víťaza</span>
            <Input style={{ width: 90 }} type="number" min="0" max="500"
              value={pts} onChange={(e) => setPts(e.target.value)} /></label>
        </>
      ) : (
        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 7 }}>
              <span className="bz-field__label" style={{ marginBottom: 0 }}>Body</span>
              {session.ai && <button type="button" onClick={aiChPoints} disabled={ptsBusy} title="Odhad bodov (AI)" aria-label="Odhad bodov"
                style={{ border: "none", background: "transparent", color: "var(--accent)", display: "inline-flex", cursor: "pointer", padding: 0 }}>{ptsBusy ? "…" : <BuzerIcon name="sparkles" size={15} color="currentColor" />}</button>}
            </div>
            <Input type="number" min="0" max="100" value={pts} onChange={(e) => setPts(e.target.value)} />
          </div>
          <Field label="Časovač" style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <Input style={{ minWidth: 0 }} type="number" min="0" max="600" value={timerMin} onChange={(e) => setTimerMin(e.target.value)} aria-label="minúty" />
              <span style={st.taskDesc}>min</span>
              <Input style={{ minWidth: 0 }} type="number" min="0" max="59" value={timerSec} onChange={(e) => setTimerSec(e.target.value)} aria-label="sekundy" />
              <span style={st.taskDesc}>s</span>
            </div>
          </Field>
        </div>
      )}
      {!isComp && (<>
        {/* v25 — Frekvencia v rovnakom dizajne ako pri návyku (segment + stepper) */}
        {(() => {
          const seg = freq > 0 ? "week" : (freqDays.length > 0 ? "custom" : "day");
          const segBtn = (val, lbl) => {
            const active = seg === val;
            return (
              <button type="button" key={val} onClick={() => {
                if (val === "day") { setFreq(0); setFreqDays([]); }
                else if (val === "week") { setFreqDays([]); setFreq(freq > 0 ? freq : 2); }
                else { setFreq(0); if (!freqDays.length) setFreqDays([1, 3, 5]); }  // v26 §3.7 — Vlastné
              }}
                style={{ flex: 1, textAlign: "center", fontSize: 12.5, fontWeight: 600, padding: "9px 0", borderRadius: "var(--radius-pill)", cursor: "pointer", border: "none", fontFamily: "var(--ff-ui)", WebkitTextFillColor: "currentColor",
                  ...(active ? { background: "var(--neutral-surface)", color: "var(--neutral-text-main)", boxShadow: "var(--elev-1)" } : { background: "transparent", color: "var(--neutral-text-muted)" }) }}>{lbl}</button>
            );
          };
          const segRow = (fill) => (
            <div style={{ display: "flex", gap: 6, background: "var(--neutral-surface-3)", padding: fill ? 5 : 4, borderRadius: fill ? 0 : "var(--radius-pill)", borderBottom: fill ? "1px solid var(--neutral-border)" : "none" }}>
              {segBtn("day", "Každý deň")}{segBtn("week", "Týždeň")}{segBtn("custom", "Vlastné")}
            </div>
          );
          return (
            <Field label="Frekvencia">
              {seg === "day" ? (
                segRow(false)
              ) : (
                <div style={{ border: "1.5px solid var(--neutral-border)", borderRadius: "var(--radius-md)", overflow: "hidden" }}>
                  {segRow(true)}
                  <div style={{ padding: 14 }}>
                    {seg === "week" ? (
                      <label style={{ ...st.inlineLabel, margin: 0 }}><span>Koľkokrát do týždňa</span>
                        <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
                          <button type="button" onClick={() => setFreq((n) => Math.max(2, n - 1))} style={st.iconBtn}>−</button>
                          <strong style={{ minWidth: 18, textAlign: "center" }}>{freq}×</strong>
                          <button type="button" onClick={() => setFreq((n) => Math.min(6, n + 1))} style={st.iconBtn}>+</button>
                        </span></label>
                    ) : (
                      <div style={{ display: "flex", gap: 4, justifyContent: "space-between" }}>
                        {WEEKDAYS_SK.map((lbl, i) => {
                          const dn = i + 1; const on = freqDays.includes(dn);
                          return (
                            <button type="button" key={dn} onClick={() => setFreqDays((p) => p.includes(dn) ? p.filter((x) => x !== dn) : [...p, dn])}
                              style={{ width: 34, height: 34, borderRadius: 99, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600, WebkitTextFillColor: "currentColor", ...(on ? { background: "var(--brand-primary)", color: "#fff" } : { background: "transparent", color: "var(--neutral-text-subtle)" }) }}>{lbl}</button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Field>
          );
        })()}
        <label style={st.inlineLabel}><span>Trvanie</span>
          <Select style={{ width: 150 }} value={durCustom ? "custom" : String(duration)}
            onChange={(e) => { const v = e.target.value; if (v === "custom") { setDurCustom(true); } else { setDurCustom(false); setDuration(Number(v)); } }}>
            <option value="0">Bez limitu</option>
            {DUR_OPTIONS.map(([d, lbl]) => <option key={d} value={String(d)}>{lbl}</option>)}
            <option value="custom">Vlastné…</option>
          </Select></label>
        {durCustom && (
          <label style={st.inlineLabel}><span>Počet dní</span>
            <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <Input style={{ width: 90 }} type="number" min="1" max="365"
                value={duration} onChange={(e) => setDuration(e.target.value)} aria-label="počet dní" />
              <span style={st.taskDesc}>dní</span>
            </span></label>
        )}
      </>)}
      {isComp && !resultTimer && (
        <label style={st.inlineLabel}><span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><BuzerIcon name="stopwatch" size={16} color="var(--text2)" /> Časovač</span>
          <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <Input style={{ width: 64 }} type="number" min="0" max="600"
              value={timerMin} onChange={(e) => setTimerMin(e.target.value)} aria-label="minúty" />
            <span style={st.taskDesc}>min</span>
            <Input style={{ width: 64 }} type="number" min="0" max="59"
              value={timerSec} onChange={(e) => setTimerSec(e.target.value)} aria-label="sekundy" />
            <span style={st.taskDesc}>s</span>
          </span></label>
      )}
      <div>
        <div style={st.fieldHint}>{editing ? "Pozvať ďalších priateľov:" : "Pozvať priateľov:"}</div>
        {invitable.length === 0 ? (
          <div style={st.taskDesc}>{editing ? "Všetci priatelia sú už vo výzve." : "Zatiaľ žiadni priatelia — pridaj si ich v záložke Priatelia."}</div>
        ) : (
          <>
            {/* G5 — search within the picker once there are many friends */}
            {invitable.length > 6 && (
              <div style={{ position: "relative", marginBottom: 8 }}>
                <BuzerIcon name="search" size={16} color="var(--neutral-text-subtle)" style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
                <Input style={{ paddingLeft: 38 }} placeholder="Hľadať priateľa…"
                  value={friendQuery} onChange={(e) => setFriendQuery(e.target.value)} />
              </div>
            )}
            <FriendPickerHint />
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, maxHeight: 200, overflowY: "auto" }}>
              {invitable.filter((f) => f.name.toLowerCase().includes(friendQuery.trim().toLowerCase())).map((f) => (
                <FriendChip key={f.friend_id} friend={f} selected={invite.includes(f.friend_id)} onToggle={toggleInv}
                  chipStyle={{ borderRadius: "var(--radius-pill)", padding: "6px 14px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "var(--ff-ui)", WebkitTextFillColor: "currentColor",
                    background: "var(--neutral-surface-2)", color: "var(--neutral-text-main)", border: "1.5px solid var(--neutral-border-strong)" }}
                  chipOnStyle={{ background: "var(--brand-primary-surface)", color: "var(--brand-primary)", border: "1.5px solid var(--brand-primary-border)" }} />
              ))}
            </div>
          </>
        )}
      </div>
      {err && <div style={st.err}>{err}</div>}

      {inventOpen && (
        <Modal center title="Vymyslieť výzvu?" icon="sparkles" onClose={() => { if (!aiBusy) setInventOpen(false); }}>
          <div style={{ fontSize: 14, color: "var(--neutral-text-muted)", lineHeight: 1.5 }}>Chceš, aby ti AI vymyslela celú výzvu? Na základe tvojich údajov navrhne názov, popis aj body.</div>
          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <Button variant="ghost" onClick={() => setInventOpen(false)} disabled={aiBusy}>Zrušiť</Button>
            <Button variant="primary" block disabled={aiBusy} onClick={async () => { await aiFill(true); setInventOpen(false); }}><BuzerIcon name="sparkles" size={16} color="currentColor" />{aiBusy ? "Vymýšľam…" : "Vymyslieť"}</Button>
          </div>
        </Modal>
      )}
      {lifeCatDialog && (
        <Modal center title="AI navrhuje kategóriu" icon="sparkles" maxWidth={360} onClose={() => setLifeCatDialog(null)}>
          <div style={{ textAlign: "center", padding: "4px 0 2px" }}>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 24, color: "var(--text)", display: "inline-flex", alignItems: "center", gap: 8 }}>
              {lifeCatDialog.busy ? "…" : (<>
                <BuzerIcon name={(LIFE_CAT[lifeCatDialog.category] || {}).icon || "label"} size={22} color="currentColor" />
                {(LIFE_CAT[lifeCatDialog.category] || {}).full || lifeCatDialog.category}
              </>)}
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <Button variant="ghost" onClick={() => setLifeCatDialog(null)}>Zrušiť</Button>
            <Button variant="primary" block disabled={lifeCatDialog.busy} onClick={() => { if (lifeCatDialog.category) setCategory(lifeCatDialog.category); setLifeCatDialog(null); }}>Aplikovať</Button>
          </div>
        </Modal>
      )}
      {ptsDialog && (
        <Modal center title="AI navrhuje" icon="sparkles" maxWidth={360} onClose={() => setPtsDialog(null)}>
          <div style={{ textAlign: "center", padding: "4px 0 2px" }}>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 26, color: "var(--text)" }}>{ptsDialog.busy ? "…" : `${ptsDialog.points} bodov`}</div>
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <Button variant="ghost" onClick={() => setPtsDialog(null)}>Zrušiť</Button>
            <Button variant="primary" block disabled={ptsDialog.busy} onClick={() => { if (ptsDialog.points != null) setPts(ptsDialog.points); setPtsDialog(null); }}>Aplikovať</Button>
          </div>
        </Modal>
      )}
      {/* G2a — kategóriu už NEblokujeme cez disabled (dead-click bez odozvy); povinnosť vynúti submit()
          handler s výrazným upozornením + zvýraznením výberu */}
      <FormFooter submitLabel={busy ? "…" : editing ? "Uložiť" : "Vytvoriť výzvu"} submitDisabled={busy || !name.trim()}
        deleteLabel="Zmazať výzvu" onDelete={editing ? del : undefined} onCancel={close} />
    </form>
    </FormShell>
    </>
  );
}

export function CompetitionDetail({ comp, reload, onClose }) {
  const { open: openPublicProfile } = useProfileModal();  // globálny modal verejného profilu
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  // v32 — graf sa ukáže až po kliknutí na účastníka v rebríčku (user_id | null); klik znova = zavrieť
  const [selectedMember, setSelectedMember] = useState(null);
  const [result, setResult] = useState("");        // today's value being entered
  const [editToday, setEditToday] = useState(true); // show the input until today's entry exists
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // v2 — multi_day: „Opraviť dnešok" prepíše; ref drží aktuálnu hodnotu pre load() (stale closure)
  const [fixToday, setFixToday] = useState(false);
  const fixTodayRef = useRef(false);
  useEffect(() => { fixTodayRef.current = fixToday; }, [fixToday]);
  const load = useCallback(async () => {
    const d = await api("/api/challenges/" + comp.id);
    setData(d);
    const me = d.members && d.members.find((m) => m.is_me);
    // v2 — multi_day: input slúži na PRIDANIE záznamu → nechaj ho prázdny;
    // počas „Opraviť dnešok" doň ale nesiahaj (stopky/refresh by zmazali rozpísanú hodnotu)
    if (d.multi_day) { if (!fixTodayRef.current) setResult(""); return; }
    // v2 — stopky: hodnoty sú sekundy → do inputu predvyplň čitateľný čas (4:35)
    if (me && me.today != null) { setResult(d.result_timer ? fmtDur(me.today) : String(me.today)); setEditToday(false); }
    else { setEditToday(true); }
  }, [comp.id]);
  useEffect(() => { load(); }, [load]);
  const closed = comp.status === "closed";
  const me = data && data.members.find((m) => m.is_me);
  // v2 — stopky merajú výsledok: hodnoty sú sekundy, zobrazujú sa ako čas (4:35 / 1:23:45)
  const isTimerComp = !!((data && data.result_timer) || comp.result_timer);
  const fmtVal = (v) => (isTimerComp ? fmtDur(v) : v);
  // v2 — multi_day: zápisy za deň sa sčítavajú (Pridať), oprava dneška prepíše
  const multiDay = !!((data && data.multi_day) || comp.multi_day);
  // uzavretá súťaž už Stopwatch nerenderuje → zahoď prípadné visiace meranie z localStorage
  useEffect(() => { if (closed) localStorage.removeItem("buzer_stopwatch_comp" + comp.id); }, [closed, comp.id]);

  async function saveResult(e, add = false) {
    e.preventDefault(); setErr("");
    const val = isTimerComp ? parseDur(result) : parseFloat(result);
    if (result === "" || isNaN(val)) { setErr(isTimerComp ? "Zadaj čas ako 4:35, 1:23:45 alebo počet sekúnd." : "Zadaj číslo."); return; }
    setBusy(true);
    try {
      await api("/api/challenges/" + comp.id + "/result", { method: "POST", body: { result: val, add } });
      setSaved(true); setEditToday(false); setFixToday(false);
      if (add) setResult("");  // multi_day: input je na ďalší záznam
      load(); reload();
      setTimeout(() => setSaved(false), 2200);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }
  // v2 — stop na stopkách: multi_day pripočíta k dnešku, inak dnešok prepíše
  async function stopwatchSave(sec) {
    setErr("");
    try {
      await api("/api/challenges/" + comp.id + "/result", { method: "POST", body: { result: sec, add: multiDay } });
      setSaved(true); load(); reload();
      setTimeout(() => setSaved(false), 2200);
    } catch (e) { setErr(e.message); throw e; }
  }
  async function toggleClose() { await api("/api/challenges/" + comp.id + "/close", { method: "POST" }); load(); reload(); }
  // v32 — opustenie aj zmazanie idú cez ConfirmDelete modal (archív ako bezpečnejšia voľba)
  const [leaveAsk, setLeaveAsk] = useState(false);
  const [delAsk, setDelAsk] = useState(false);
  async function archiveMe() { await api("/api/challenges/" + comp.id + "/archive", { method: "POST", body: { scope: "me" } }); reload(); onClose && onClose(); }
  async function leave() { await api("/api/challenges/" + comp.id + "/leave", { method: "DELETE" }); reload(); onClose && onClose(); }
  async function archiveAll() { await api("/api/challenges/" + comp.id + "/archive", { method: "POST", body: { scope: "all" } }); reload(); onClose && onClose(); }
  async function del() { await api("/api/challenges/" + comp.id, { method: "DELETE" }); reload(); onClose && onClose(); }

  if (editing && data) {
    return (
      <ChallengeCreate challenge={{ id: comp.id, name: data.name, icon: data.icon, icon_color: data.icon_color, desc: data.descr,
        points: data.points, is_competition: true, metric_label: data.metric_label, category: data.category,
        metric_dir: data.metric_dir, eval_date: data.eval_date, timer_sec: data.timer_sec, result_timer: data.result_timer, multi_day: data.multi_day }}
        memberIds={data.members.map((m) => m.user_id)}
        onAdded={() => { load(); reload(); }} onDone={() => setEditing(false)} />
    );
  }

  // v32 — 7-dňový graf účastníka (rovnaký chart element, aký mal „Tvoje záznamy · 7 dní");
  // renderuje sa priamo pod kliknutým riadkom rebríčka
  const memberChart = (m) => {
    const hist = m.history || {};
    const days = Object.keys(hist).sort();
    if (!days.length) return null;
    const maxV = Math.max(1, ...days.map((k) => hist[k]));
    const lbls = ["Ne", "Po", "Ut", "St", "Št", "Pi", "So"];
    return (
      <div style={{ margin: "-4px 0 10px" }}>
        <SectionHeader label={(m.is_me ? "Tvoje záznamy" : m.name) + " · 7 dní"} style={{ margin: "6px 2px 4px" }}
          trailing={m.result != null ? <span style={{ ...st.taskDesc, fontFamily: "var(--font-mono)", fontWeight: 700 }}>súčet {fmtVal(m.result)}</span> : null} />
        {/* v18 — chart inside a bordered card (mock #12) */}
        <div style={{ background: "var(--surface-solid)", border: "1px solid var(--border)", borderRadius: 18, padding: "16px 12px 10px" }}>
          <div style={st.chart}>
            {days.map((k) => {
              const v = hist[k];
              const dt = new Date(k + "T00:00:00");
              return (
                <div key={k} style={st.chartCol}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: "var(--text2)" }}>{v ? fmtVal(v) : ""}</span>
                  <div style={st.barTrack}><div style={{ ...st.bar, height: `${Math.max(4, (v / maxV) * 100)}%`, background: v > 0 ? "var(--accent-grad)" : "var(--accent-tint)" }} /></div>
                  <span style={st.chartLabel}>{lbls[dt.getDay()]}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* v26 §3.12 — popis v rámiku hore */}
      <DescrBox text={data && data.descr} style={{ marginTop: 10 }} />
      {/* v32 — riadok dlaždíc: životná kategória + koľko zostáva do konca (v33 — jednotný life_cat) */}
      {data && ((LIFE_CAT[data.life_cat || data.category]) || data.days_left != null || data.points != null) && (
        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          {LIFE_CAT[data.life_cat || data.category] && (
            <Stat icon={<BuzerIcon name={LIFE_CAT[data.life_cat || data.category].icon} size={17} color="currentColor" />} value={LIFE_CAT[data.life_cat || data.category].label} label="KATEGÓRIA" />
          )}
          {data.days_left != null && (
            <Stat icon={<BuzerIcon name="hourglass" size={17} color="currentColor" />} value={`${data.days_left} ${data.days_left === 1 ? "deň" : (data.days_left >= 2 && data.days_left <= 4 ? "dni" : "dní")}`} label="DO KONCA" />
          )}
          {/* v33h — body pre víťaza (odmena za 1. miesto) vedľa „do konca" */}
          {data.points != null && (
            <Stat icon={<span style={{ fontFamily: "var(--ff-mono)", fontWeight: 700, fontSize: 17, lineHeight: 1, color: "var(--brand-primary)" }}>+</span>} value={data.points} valueColor="var(--brand-primary)" label="PRE VÍŤAZA" />
          )}
        </div>
      )}

      {(data ? (
        <>
          <SectionHeader label="Rebríček · podľa súčtu" icon="trophy" style={{ margin: "20px 2px 12px" }} />
          <div>
            {data.members.map((m) => {
              const sel = selectedMember === m.user_id;
              const leaderResult = data.members.find((x) => x.rank === 1)?.result;
              // v33 — okrem súčtu a počtu záznamov teraz vidno aj dnešný prírastok a (pri ostatných
              // účastníkoch) odstup za vedúcim — predtým backend `today` posielal, ale nikde sa neukázal
              const metaTxt = m.result != null ? [
                (isTimerComp ? fmtDur(m.result) : m.result + " " + data.metric_label),
                (m.entries || 0) + "×",
                m.today != null ? "dnes " + fmtVal(m.today) : null,
                (m.rank !== 1 && leaderResult != null) ? fmtVal(Math.abs(leaderResult - m.result)) + " za vedúcim" : null,
              ].filter(Boolean).join(" · ") : "zatiaľ bez záznamu";
              return (
                <div key={m.user_id}>
                  {/* v32 — klik na riadok prepína 7-dňový graf účastníka (namiesto otvárania profilu) */}
                  <div role="button" tabIndex={0} aria-expanded={sel} aria-label={"Zobraziť záznamy: " + m.name}
                    onClick={() => setSelectedMember((p) => (p === m.user_id ? null : m.user_id))}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelectedMember((p) => (p === m.user_id ? null : m.user_id)); } }}
                    style={{ display: "flex", alignItems: "center", gap: 11, padding: "14px 14px", borderRadius: 16, marginBottom: 10, cursor: "pointer", border: "1px solid " + (sel ? "var(--accent)" : (m.is_me || m.is_winner ? "var(--accent-tint)" : "var(--border)")), background: m.is_me || m.is_winner ? "var(--ember-soft)" : "var(--surface-solid)" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: 8, flexShrink: 0, fontFamily: "var(--font-mono)", fontWeight: 800, fontSize: 12.5,
                      ...(m.rank === 1 ? { background: "var(--accent)", color: "var(--on-ember)" } : { background: "var(--surface)", border: "1px solid var(--border)", color: "var(--text2)" }) }}>{m.is_winner ? "🏆" : (m.rank || "–")}</span>
                    {/* B6 — klik na avatar otvorí verejný profil (bez prepnutia grafu) */}
                    <Avatar user={m} size={30} onClick={(e) => { e.stopPropagation(); openPublicProfile(m.user_id); }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ ...st.boardName, display: "inline-flex", alignItems: "center", gap: 6 }}>{m.name}{m.is_me ? <Tag tone="neutral" size="xs">TY</Tag> : null}</div>
                      <div style={st.taskDesc}>{metaTxt}</div>
                    </div>
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 14, fontWeight: 800, flexShrink: 0, color: m.rank === 1 ? "var(--accent)" : "var(--text2)" }}>{m.result != null ? fmtVal(m.result) : "–"}</span>
                    <Icon name="chevron" size={15} color="var(--muted)" style={{ flexShrink: 0, transform: sel ? "rotate(90deg)" : "none", transition: "transform .18s ease" }} />
                  </div>
                  {sel && memberChart(m)}
                </div>
              );
            })}
          </div>
          {data.invited_count > 0 && <div style={{ ...st.taskDesc, marginTop: 4, display: "inline-flex", alignItems: "center", gap: 5 }}><BuzerIcon name="hourglass" size={13} color="currentColor" />{data.invited_count} pozvaných</div>}

          {/* v33h — tvorcove akcie (Upraviť/Vyhodnotiť/kôš) sú v sticky footeri nižšie; tu ostáva
              len „Opustiť" pre nečlena-tvorcu */}
          {!comp.is_creator && (
            <div style={{ borderTop: "1px solid var(--neutral-border)", marginTop: 18, paddingTop: 14, display: "flex", gap: 8 }}>
              <Button variant="ghost" block onClick={() => setLeaveAsk(true)} style={{ color: "var(--danger)" }}><Icon name="logout" size={16} color="currentColor" />Opustiť výzvu</Button>
            </div>
          )}
          {/* v32 — opustenie: archív u mňa (primary) alebo odchod bez výsledkov */}
          {leaveAsk && (
            <ConfirmDelete title="Opustiť výzvu?" icon="logout"
              message={<>Ak výzvu archivuješ, odídeš z nej, ale <strong>výsledky ti ostanú v archíve</strong>. Pri opustení sa tvoje výsledky u teba neuložia.</>}
              archiveLabel="Archivovať u mňa" onArchive={async () => { setLeaveAsk(false); await archiveMe(); }}
              deleteLabel="Opustiť" onDelete={async () => { setLeaveAsk(false); await leave(); }}
              onClose={() => setLeaveAsk(false)} />
          )}
          {/* v32 — zmazanie tvorcom: archív pre všetkých (primary), alebo zmazať u mňa —
              ostatným účastníkom sa výzva automaticky archivuje s výsledkami */}
          {delAsk && (
            <ConfirmDelete title="Zmazať výzvu?" icon="trash"
              message={<>Archivovaním sa výzva ukončí k dnešku a <strong>všetkým ostane v archíve</strong>. Zmazaním zmizne u teba — ostatným účastníkom sa <strong>automaticky archivuje</strong> s výsledkami.</>}
              archiveLabel="Archivovať pre všetkých" onArchive={async () => { setDelAsk(false); await archiveAll(); }}
              deleteLabel="Zmazať u mňa" onDelete={async () => { setDelAsk(false); await del(); }}
              onClose={() => setDelAsk(false)} />
          )}

          {/* v32 — sticky footer v tele sheetu: zápis dnešného výsledku + Uložiť, pod tým časovač.
              Rebríček + grafy scrollujú nad ním (trieda .bz-sheet-stickyfoot v primitives.css). */}
          {(comp.is_creator || !closed) && (
          <div className="bz-sheet-stickyfoot">
            {!closed && (<>
            {/* v2 — multi_day: „Pridať" sčítava záznamy; „Opraviť dnešok" prepíše dnešný súčet */}
            {multiDay && (
              <>
                <form onSubmit={(e) => saveResult(e, !fixToday)}>
                  <Caption style={{ display: "block", margin: "0 2px 6px" }}>{fixToday ? "Opraviť dnešný súčet" : "Pridať dnešný záznam"} · {isTimerComp ? "čas" : comp.metric_label}</Caption>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {isTimerComp
                      ? <Input style={{ flex: 1, minWidth: 0 }} type="text" inputMode="text"
                          value={result} onChange={(e) => { setResult(e.target.value); setSaved(false); }} placeholder="napr. 4:35 alebo 1:23:45" />
                      : <Input style={{ flex: 1, minWidth: 0 }} type="number" step="any"
                          value={result} onChange={(e) => { setResult(e.target.value); setSaved(false); }} placeholder={comp.metric_label} />}
                    <Button variant="primary" type="submit" disabled={busy}>{busy ? "…" : (fixToday ? "Uložiť" : "Pridať")}</Button>
                    {fixToday && <Button variant="ghost" type="button" onClick={() => { setFixToday(false); setResult(""); }}>Zrušiť</Button>}
                  </div>
                </form>
                {me && me.today != null && !fixToday && (
                  <div style={st.resultEntered}>
                    <span style={{ fontSize: 14, display: "inline-flex", alignItems: "center", gap: 5 }}><Icon name="check" size={13} color="var(--accent)" /> Dnes: <strong>{isTimerComp ? fmtDur(me.today) : me.today + " " + comp.metric_label}</strong>{me.result != null ? <span style={st.taskDesc}> · spolu {fmtVal(me.result)}</span> : null}</span>
                    <button type="button" onClick={() => { setFixToday(true); setResult(isTimerComp ? fmtDur(me.today) : String(me.today)); }} style={st.linkBtn}>Opraviť dnešok</button>
                  </div>
                )}
              </>
            )}
            {!multiDay && (editToday ? (
              <form onSubmit={saveResult}>
                <Caption style={{ display: "block", margin: "0 2px 6px" }}>Tvoj dnešný výsledok · {isTimerComp ? "čas" : comp.metric_label}</Caption>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  {isTimerComp
                    ? <Input style={{ flex: 1, minWidth: 0 }} type="text" inputMode="text"
                        value={result} onChange={(e) => { setResult(e.target.value); setSaved(false); }} placeholder="napr. 4:35 alebo 1:23:45" />
                    : <Input style={{ flex: 1, minWidth: 0 }} type="number" step="any"
                        value={result} onChange={(e) => { setResult(e.target.value); setSaved(false); }} placeholder={comp.metric_label} />}
                  <Button variant="primary" type="submit" disabled={busy}>{busy ? "…" : "Uložiť"}</Button>
                  {me && me.today != null && <Button variant="ghost" type="button" onClick={() => { setEditToday(false); setResult(isTimerComp ? fmtDur(me.today) : String(me.today)); }}>Zrušiť</Button>}
                </div>
              </form>
            ) : (
              <div style={st.resultEntered}>
                <span style={{ fontSize: 14, display: "inline-flex", alignItems: "center", gap: 5 }}><Icon name="check" size={13} color="var(--accent)" /> Dnes: <strong>{me && (isTimerComp ? fmtDur(me.today) : me.today + " " + comp.metric_label)}</strong>{me && me.result != null ? <span style={st.taskDesc}> · spolu {fmtVal(me.result)}</span> : null}</span>
                <button type="button" onClick={() => { setEditToday(true); }} style={st.linkBtn}>Upraviť dnešok</button>
              </div>
            ))}
            {saved && <div style={{ color: "var(--success)", fontSize: 13, fontWeight: 700 }}><Chk color="var(--success)" />Uložené</div>}
            {err && <div style={st.err}>{err}</div>}
            {/* v2 — stopky merajú výsledok: stop → auto-uloženie (pripočíta sa k dnešku) */}
            {isTimerComp && <Stopwatch id={"comp" + comp.id} onStop={stopwatchSave} />}
            {/* časovač na výkon: primárne tlačidlo „Spustiť časovač" ROVNAKO ako v detaile návyku
                (variant="button"); po spustení sa prepne na sekundárne. */}
            {!isTimerComp && (comp.timer_sec || comp.timer_min) > 0 && (
              <Timer variant="button" id={"comp" + comp.id} seconds={comp.timer_sec || (comp.timer_min || 0) * 60} />
            )}
            </>)}
            {/* v33h — tvorcove akcie (Upraviť · Vyhodnotiť · kôš) ukotvené v sticky footeri, aj po vyhodnotení */}
            {comp.is_creator && (
              <div style={{ display: "flex", gap: 8, alignItems: "center", ...(!closed ? { marginTop: 10, borderTop: "1px solid var(--neutral-border)", paddingTop: 10 } : {}) }}>
                <Button variant="ghost" block onClick={() => setEditing(true)}><Icon name="edit" size={16} color="currentColor" />Upraviť</Button>
                <Button variant="secondary" block onClick={toggleClose}>{closed ? <><Icon name="refresh" size={16} color="currentColor" />Znovu otvoriť</> : <><Icon name="check" size={16} color="currentColor" stroke={2.6} />Vyhodnotiť</>}</Button>
                <IconButton variant="ghost" aria-label="Zmazať výzvu" title="Zmazať výzvu" onClick={() => setDelAsk(true)} style={{ color: "var(--danger)" }}><Icon name="delete" size={16} color="currentColor" /></IconButton>
              </div>
            )}
          </div>
          )}
        </>
      ) : <div style={{ display: "flex", justifyContent: "center", padding: 10 }}><div className="spin" /></div>)}
    </div>
  );
}
